import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'

import AddCircle from '@mui/icons-material/AddCircle'
import Download from '@mui/icons-material/Download'
import Close from '@mui/icons-material/HighlightOff'
import PersonAdd from '@mui/icons-material/PersonAdd'
import PlaylistAddOutlined from '@mui/icons-material/PlaylistAddOutlined'

import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import debounce from '@mui/material/utils/debounce'

import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import AddUsersToGroupDialog from '@/components/AddUsersToUserGroupDialog'
import CreateGroupDialog from '@/components/CreateGroupDialog'
import UploadListDialog from '@/components/UploadListDialog'
import { useNavigate } from 'react-router-dom'
import usePermissions from '@/hooks/usePermissions'
import PermissionsController from '@/components/PermissionsController'

const mapStateToProps = ({ userGroup, loading }) => ({
  userGroupList: userGroup.userGroupList,
  isLoading: loading['userGroup/getUserGroups'],
  deleteLoading: loading['userGroup/deleteUserGroup'],
})

const fieldMap = {
  groupName: 'GROUP_NAME',
  groupDesc: 'GROUP_DESC',
  totalUsers: 'USER_COUNT',
  lastModify: 'LAST_MODIFY',
}

const UserGroupManagement = ({ dispatch, userGroupList, isLoading, deleteLoading }) => {
  const { data, paging = {} } = userGroupList || {}

  const { t } = useTranslation()
  const navigate = useNavigate()

  const [userGroupName, setUserGroupName] = useState()
  const [userGroupOptions, setUserGroupOptions] = useState([])
  const [sort, setSort] = useState([{ field: 'lastModify', sort: 'desc' }])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteGroupData, setDeleteGroupData] = useState()
  const [addUserGroupData, setAddUserGroupData] = useState()

  const [showUpload, setShowUpload] = useState(false)
  const [csvData, setCsvData] = useState()

  const canModify = usePermissions('userGroup_modify')
  const canDelete = usePermissions('userGroup_delete')

  const form = useRef()
  const csvRef = useRef()

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const setUserGroupNameDebounce = debounce((_, value) => {
    setUserGroupName(value)
  }, 400)

  const downloadUserGroup = ({ groupId, groupName }) => {
    dispatch({
      type: 'userGroup/userGroupDownload',
      payload: { groupId },
      successCallback: response => {
        setCsvData({ filename: `${groupName}_download_data.csv`, data: response })
      },
    })
  }

  const btnProps = {
    size: 'small',
    sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
  }
  const iconProps = {
    sx: {
      padding: 0.5,
    },
  }

  const cols = [
    {
      field: 'groupName',
      headerName: t('general.user_group_name'),
      width: 200,
      renderCell: ({ row }) => (
        <Button
          variant="text"
          onClick={() => {
            navigate(`/group_management/${row.groupId}`, { state: { name: '' } })
          }}
        >
          {row.groupName}
        </Button>
      ),
    },
    {
      field: 'groupDesc',
      headerName: t('general.user_group_desc'),
      width: 200,
    },
    {
      field: 'totalUsers',
      headerName: t('general.total_count'),
      width: 100,
    },
    {
      field: 'lastModify',
      headerName: t('general.last_data_modify'),
      width: 200,
      valueFormatter: ({ value }) => (value ? moment(value).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      field: 'add',
      hidden: !canModify,
      headerName: t('general.add'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            setAddUserGroupData(row)
          }}
        >
          <PersonAdd {...iconProps} />
        </IconButton>
      ),
    },
    {
      field: 'download',
      headerName: t('general.download'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            downloadUserGroup(row)
          }}
        >
          <Download {...iconProps} />
        </IconButton>
      ),
    },
    {
      field: 'delete',
      hidden: !canDelete,
      headerName: t('general.delete'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            setDeleteGroupData(row)
          }}
        >
          <Close {...iconProps} />
        </IconButton>
      ),
    },
  ]

  const fetchData = useCallback(
    userGroupValue => {
      // TODO: backend not support searchParam
      // const { search, userGroup } = formValues || {};
      let payload = {
        // searchParam: search,
        sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
        pageIndex: page,
        pageSize,
      }

      const searchGroupName = userGroupValue || form.current?.getValues()?.userGroup?.label

      if (searchGroupName) {
        payload = {
          ...payload,
          searchGroupName,
        }
      }

      dispatch({
        type: 'userGroup/getUserGroups',
        payload,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort, page, pageSize, form],
  )

  const deleteUserGroup = () => {
    if (deleteGroupData) {
      dispatch({
        type: 'userGroup/deleteUserGroup',
        payload: { groupId: deleteGroupData.groupId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.user_group_deleted', { name: deleteGroupData.groupName }),
              severity: 'success',
            },
          })
          setDeleteGroupData()
          fetchData()
        },
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [sort, page, pageSize, fetchData])

  useEffect(() => {
    if (userGroupName) {
      dispatch({
        type: 'userGroup/getUserGroupsByName',
        payload: { searchUserGroupName: userGroupName },
        successCallback: options => {
          const transformedOptions = Object.keys(options).map(key => ({
            label: options[key],
            id: key,
          }))
          if (transformedOptions) {
            setUserGroupOptions(transformedOptions)
          }
        },
      })
    } else {
      setUserGroupOptions([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroupName])

  useEffect(() => {
    if (csvData) {
      csvRef.current?.link?.click?.()
    }
  }, [csvData])

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2} my={1}>
        <PermissionsController permission={'userGroup_create'}>
          <Grid item mr={2}>
            <Button
              variant="text"
              color="secondary_80"
              endIcon={<AddCircle sx={{ color: '#2D2D6C80' }} />}
              onClick={() => {
                setCreateDialogOpen(true)
              }}
            >
              {t('general.add_user_group')}
            </Button>
          </Grid>
        </PermissionsController>

        <PermissionsController permission={'userGroup_create'}>
          <Grid item mr={2}>
            <Button
              variant="text"
              color="secondary_80"
              endIcon={<PlaylistAddOutlined sx={{ color: '#2D2D6C80' }} />}
              onClick={() => {
                setShowUpload(true)
              }}
            >
              {t('general.upload_name_batch')}
            </Button>
          </Grid>
        </PermissionsController>
        <Grid item>
          <FormCreator
            ref={form}
            formItems={[
              // {
              //   name: 'search',
              //   placeholder: t('general.insert_keyword'),
              //   defaultValue: '',
              //   inputConfig: {
              //     onSubmit: async () => {
              //       setPage(0);
              //       fetchData();
              //     },
              //   },
              // },
              {
                type: 'autocomplete',
                label: t('general.user_group_select'),
                name: 'userGroup',
                defaultValue: '',
                inputConfig: {
                  onChange: selectedOption => {
                    setPage(0)
                    fetchData(selectedOption?.label)
                  },
                  onInputChange: setUserGroupNameDebounce,
                  options: userGroupOptions,
                  disableClearable: true,
                  isOptionEqualToValue: (option, value) => option.id === value.id,
                  placeholder: t('general.order_by_alphabet'),
                },
              },
            ]}
            flexDirection="row"
            submitComponent={() => null}
            resetComponent={() => null}
            gridItemProps={{ mr: 2 }}
          />
        </Grid>
        <Grid item>
          <Button
            variant="text"
            color="secondary_80"
            endIcon={<Close sx={{ color: '#2D2D6C80', stroke: '#2D2D6C80', strokeWidth: 2 }} />}
            onClick={() => {
              form.current?.setValue?.('search', '')
              form.current?.setValue?.('userGroup', '')
              fetchData()
            }}
          >
            {t('general.clear_conditions')}
          </Button>
        </Grid>
      </Grid>
      <Table
        minHeight={400}
        columns={cols}
        rows={data || []}
        dataGridConfig={{
          sortingMode: 'server',
          sortingOrder: ['desc', 'asc', null],
          sortModel: sort,
          onSortModelChange: newSort => setSort(newSort),
          getRowId: row => row.groupId,
          loading: isLoading,
        }}
        noRowsLabel={t('errors.no_user_groups')}
        pagination={pagination}
      />

      <CreateGroupDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false)
        }}
        onComplete={() => {
          fetchData()
        }}
      />

      <UploadListDialog
        open={showUpload}
        onClose={() => {
          setShowUpload(false)
        }}
        onComplete={() => {
          fetchData()
        }}
      />

      <ConfirmDialog
        title={t('messages.confirm_delete')}
        open={!!deleteGroupData}
        onClose={() => {
          setDeleteGroupData()
        }}
        onSubmit={() => {
          deleteUserGroup()
        }}
        loading={deleteLoading}
      >
        <Typography>
          {t('messages.confirm_delete_user_group', { name: deleteGroupData?.groupName })}
        </Typography>
      </ConfirmDialog>

      <AddUsersToGroupDialog
        data={addUserGroupData}
        onClose={() => {
          setAddUserGroupData()
        }}
        onComplete={() => {
          fetchData()
        }}
      />

      <CSVLink ref={csvRef} filename={csvData?.filename || ''} data={csvData?.data || []} />
    </Container>
  )
}

export default connect(mapStateToProps)(UserGroupManagement)
